import React, { useEffect, useState } from 'react';
import { Col, Row, Space, Card, Table, Input, Modal, Typography } from 'antd';
import { Filter } from 'iconsax-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { Search } from 'react-iconly';
import { useIntl } from 'react-intl';
import { RiAddLine, RiCloseFill } from 'react-icons/ri';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import basePagination from '../../../../../const/pagination';
import urlPageCategoryCreate from '../../../../../urls/urlPageCategoryCreate';
import urlPageCategoryEdit from '../../../../../urls/urlPageCategoryEdit';
import { PrivateComponent } from '../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../const/permissions';
import { getCategoriesTableColumns } from './PageCategories.const';
import useElementSize from '../../../../../app/hooks/useElementSize';
import useToggle from '../../../../../app/hooks/useToggle';
import useQueryParams from '../../../../../app/hooks/useQueryParams';
import makeTableLoadingObject from '../../../../../lib/makeTableLoadingObject';
import handleResponse from '../../../../../lib/handleResponse';
import applyFilter from '../../../../../lib/applyFilter';
import resetFilter from '../../../../../lib/resetFilter';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import DocumentationTooltip from '../../../../../components/DocumentationTooltip/DocumentationTooltip';
import ActionButton from '../../../../../layout/components/action-button';
import Sidebar from '../../../../../components/Sidebar';
import TotalLabel from '../../../../../components/TotalLabel';
import FormCategoriesFilter from '../../../forms/FormCategoriesFilter';
import PreviewDictionaryTranslation from '../../../../Translations/components/PreviewDictionaryTranslation/PreviewDictionaryTranslation';
import { useGetLanguagesQuery } from '../../../../Translations/api/languagesApiSlice';
import {
  useGetCategoriesQuery,
  useDeleteCategoryMutation,
} from '../../../api/categoriesApiSlice';



const { Title } = Typography;

const PageCategories = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [ elementRef, size ] = useElementSize();
  const [ searchQueryParams, setSearchParams ] = useSearchParams();
  const [ previewImage, setPreviewImage ] = useState('');
  const [ previewModalOpen, togglePreviewModal ] = useToggle();
  const [ previewDictionaryId, setPreviewDictionaryId ] = useState({});
  const [ openDictionaryModal, toggleDictionaryModal ] = useToggle();
  const [ filterOpen, toggleFilterSidebar ] = useToggle();

  const searchFields = [ 'title' ];
  const searchFilterFields = [ 'id', 'client_id', 'title', 'is_published', 'template' ];

  const {
    pagination,
    searchParams,
    initFilterValues,
    setInitFilterValues,
    search,
    setSearchTerm,
    handleChangeTableParams,
    resetIsManuallyTriggered,
    setResetManuallyTriggered,
  } = useQueryParams({ searchFields, searchFilterFields });

  const searchParamsString = searchParams.toString();

  const { data: {
    data: categories = [],
    pagination: dataPagination = {},
  } = { data: [], pagination: {} },
  isFetching,
  error: formErrors = {},
  } = useGetCategoriesQuery(
    { queryParams: `${searchParamsString}&include=dictionary,template,dynamic_data` },
    { skip: !searchParamsString.includes('limit') },
  );

  const { data: {
    data: languages = [],
  } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' });

  const [ deleteCategory, { isLoading: isDeletingCategory } ] = useDeleteCategoryMutation();

  useEffect(() => {
    localStorage.setItem('indexSearchParams', searchParamsString);
  }, [ searchParams ]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setResetManuallyTriggered(false);
  };

  const handleImageCardClick = (preview) => {
    setPreviewImage(preview);
    togglePreviewModal();
  };

  const handlePreviewModalClose = () => {
    togglePreviewModal();
  };

  const handlePreviewDictionary = (dictionaryId) => {
    setPreviewDictionaryId(dictionaryId);
    toggleDictionaryModal();
  };

  const handleCreate = () => {
    navigate(urlPageCategoryCreate());
  };

  const handleEdit = (categoryId) => {
    navigate(urlPageCategoryEdit({ categoryId }));
  };

  const handleDelete = (id) => {
    deleteCategory(id)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const isLoading = isFetching || isDeletingCategory;

  return (
    <>
      <Modal
        width={500}
        title={intl.formatMessage({ id: 'content-image-preview-card-title' })}
        centered
        visible={previewModalOpen}
        onCancel={handlePreviewModalClose}
        footer={false}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <img alt="icon" src={previewImage} />
      </Modal>

      <Modal
        title={<IntlMessages id="dictionaries-translation-preview" />}
        width="60%"
        centered
        destroyOnClose
        visible={openDictionaryModal}
        onCancel={toggleDictionaryModal}
        footer={null}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <PreviewDictionaryTranslation
          dictionaryId={previewDictionaryId}
          languages={languages}
        />
      </Modal>

      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbActive={
            <div>
              <IntlMessages id='categories-breadcrumbs' />
              <DocumentationTooltip link="https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/668893191/Categories" />
            </div>
          }
        />

        <Col>
          <Space>
            <TotalLabel total={dataPagination.total ?? 0} />
            <Col>
              <Input
                data-testid="search-input"
                allowClear={!isEmpty(search)}
                placeholder={intl.formatMessage({ id: 'ui-general-search-by-title' })}
                prefix={<Search set="curved" size={16} className="hp-text-color-black-80" />}
                value={search}
                onChange={handleSearch}
              />
            </Col>
            <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.CREATE ]}>
              <ActionButton
                onClick={handleCreate}
                icon={<RiAddLine className="btn-icon-mr-1" />}
                title={<IntlMessages id='ui-general-create' />}
              />
            </PrivateComponent>
            <ActionButton
              title=""
              isActive={!isEmpty(initFilterValues)}
              icon={<Filter size={18} data-testid="filter-action-icon" />}
              onClick={toggleFilterSidebar}
            />
          </Space>
        </Col>

        <Sidebar
          title={<Title level={5}>{intl.formatMessage({ id: 'categories-filter-title' })}</Title>}
          visible={filterOpen}
          toggleSidebar={toggleFilterSidebar}
        >
          <FormCategoriesFilter
            isSubmitting={isLoading}
            formErrors={formErrors}
            initialValues={initFilterValues}
            onCancel={() => {
              setResetManuallyTriggered(true);

              resetFilter({
                searchQueryParams,
                setSearchParams,
                setInitFilterValues,
                resetIsManuallyTriggered,
                setSearchTerm,
              });
            }}
            onSubmit={(values) => {
              applyFilter({
                values,
                searchQueryParams,
                searchFilterFields,
                setSearchParams,
                setSearchField: setSearchTerm,
                toggleFilterSidebar,
              });
            }}
          />
        </Sidebar>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <div ref={elementRef}>
              <Table
                sticky
                loading={makeTableLoadingObject(isLoading)}
                rowKey="id"
                columns={
                  getCategoriesTableColumns(
                    handleImageCardClick,
                    handlePreviewDictionary,
                    handleEdit,
                    handleDelete,
                    size,
                  )
                }
                dataSource={categories}
                onChange={handleChangeTableParams}
                pagination={{
                  ...basePagination,
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: dataPagination?.total,
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageCategories;
